import { z } from 'zod'

// ---------------------------------------------------------------------------
// `GET /api/v1/reference/vocabulary` — the policy vocabulary pack.
//
// Mirrors `types/api/contract.ts` §5 in the platform repo (see the note at the
// top of `query.ts` about why the wire shape is transcribed verbatim rather
// than restyled).
//
// Two fields here exist specifically for the Ask surface:
//  - `aggregatable`, required on every field type, is what makes an aggregate
//    query legal over a field. It is required rather than optional so that a
//    new field has to *say* whether it can be summed; defaulting would let a
//    field become aggregatable — or stop being — by oversight.
//  - `knownLimitations` is interpolated verbatim into the classifier prompt,
//    and is what lets Ask refuse with a specific reason instead of guessing.
//    Required, because a pack declaring no limitations is making a claim, not
//    omitting one.
// ---------------------------------------------------------------------------

const isoSchema = z.iso.datetime({ offset: true })

export interface FieldType {
  type: string
  aggregatable: boolean
  values?: string[]
  of?: FieldType
  to?: string
}

/** Recursive: a `list` field declares the type of what it holds. */
export const fieldTypeSchema: z.ZodType<FieldType> = z.lazy(() =>
  z.union([
    z
      .object({
        type: z.enum(['string', 'number', 'boolean', 'currency', 'iso4217', 'date', 'datetime']),
        aggregatable: z.boolean(),
      })
      .strict(),
    z
      .object({
        type: z.literal('enum'),
        values: z.array(z.string()),
        aggregatable: z.boolean(),
      })
      .strict(),
    z.object({ type: z.literal('list'), of: fieldTypeSchema, aggregatable: z.boolean() }).strict(),
    z.object({ type: z.literal('ref'), to: z.string(), aggregatable: z.boolean() }).strict(),
  ]),
)

export const factTypeSchema = z
  .object({
    description: z.string(),
    fields: z.record(z.string(), fieldTypeSchema),
  })
  .strict()
export type FactType = z.infer<typeof factTypeSchema>

export const predicateDeclarationSchema = z
  .object({
    label: z.string(),
    category: z.string(),
    inputs: z.array(
      z
        .object({
          name: z.string(),
          type: z.string(),
          description: z.string().optional(),
        })
        .strict(),
    ),
    logic_description: z.string(),
    evaluator: z
      .object({ type: z.enum(['builtin', 'expression']), reference: z.string() })
      .strict(),
  })
  .strict()
export type PredicateDeclaration = z.infer<typeof predicateDeclarationSchema>

export const vocabularyPackSchema = z
  .object({
    id: z.string(),
    version: z.string(),
    runtime_compatibility: z.string(),
    description: z.string(),
    factTypes: z.record(z.string(), factTypeSchema),
    predicates: z.record(z.string(), predicateDeclarationSchema),
    actionTypes: z.array(z.string()),
    authoringUIHints: z
      .object({
        categories: z.array(z.string()),
        commonRules: z.array(
          z.object({ name: z.string(), description: z.string(), template: z.string() }).strict(),
        ),
      })
      .strict(),
    /** Keyed by a stable short id, never a sentence. Values are interpolated into the Ask prompt verbatim. */
    /**
     * ADDED BY CADENCE 2026-08-23 — and it exists because of this console.
     *
     * The published pack is a strict SUBSET of what the API actually serves, and
     * a consumer reading the file alone treats that subset as the whole
     * queryable surface. It then rejects fields the API accepts — which is
     * precisely what happened to the Ask classifier, and why
     * `RUN_LEVEL_GROUPINGS` exists in `ask/service.ts` as a stopgap.
     *
     * `isSubset` says so out loud rather than leaving a consumer to discover it
     * by refusing a valid question.
     */
    completeness: z
      .object({
        isSubset: z.boolean(),
        thisFileDeclares: z.string(),
        alsoServed: z.string(),
        wholeVocabularyAt: z.string(),
        warning: z.string(),
      })
      .strict(),
    knownLimitations: z.record(z.string(), z.string()),
  })
  .strict()
export type VocabularyPack = z.infer<typeof vocabularyPackSchema>

export const vocabularyResponseSchema = z
  // `tenant_id` per CAD-008 — see `contract-read.ts`'s `withAsOf`.
  .object({
    data: vocabularyPackSchema,
    tenant_id: z.string(),
    tenant_source: z.enum(['header', 'deployment-binding']),
    as_of: isoSchema,
  })
  .strict()
export type VocabularyResponse = z.infer<typeof vocabularyResponseSchema>

// ---------------------------------------------------------------------------
// Lookups the Ask validator needs. Kept here, beside the schema they read,
// rather than in the service — they are facts about the vocabulary's shape.
// ---------------------------------------------------------------------------

/**
 * Finds a field by name across every fact type. Cadence's own implementation
 * does first-match-wins across fact types and notes that no two currently
 * share a field name; this mirrors that so the console and the query engine
 * agree on which field a name refers to. If packs ever do collide on a name,
 * both sides have to change together.
 */
export function findVocabularyField(pack: VocabularyPack, name: string): FieldType | null {
  for (const factType of Object.values(pack.factTypes)) {
    const field = factType.fields[name]
    if (field !== undefined) return field
  }
  return null
}

export function isAggregatableField(pack: VocabularyPack, name: string): boolean {
  return findVocabularyField(pack, name)?.aggregatable === true
}

/** Every field name the vocabulary declares — what a `groupBy` or `field` reference is checked against. */
export function vocabularyFieldNames(pack: VocabularyPack): string[] {
  return Object.values(pack.factTypes).flatMap((factType) => Object.keys(factType.fields))
}
